import React, { useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { getFileServerBase } from './fileServerBase';

const FILE_SERVER_BASE = `${getFileServerBase()}/media/large/`;

const resolveUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${FILE_SERVER_BASE}${url}`;
};

// ─── TYPES ───────────────────────────────────────────────────────

type AccordionItemMode = 'text' | 'steps';

interface AccordionStep {
  id: string;
  text: string;
  iconFilename: string;
  iconUrl: string;
}

interface AccordionStepGroup {
  id: string;
  label: string;
  intro: string;
  steps: AccordionStep[];
}

interface AccordionItem {
  id: string;
  title: string;
  mode: AccordionItemMode;
  body: string;
  groups: AccordionStepGroup[];
}

interface AccordionBlockRendererProps {
  config: {
    accordion_items?: AccordionItem[];
  };
}

// ─── MIGRATION ───────────────────────────────────────────────────

const migrateItem = (raw: any): AccordionItem => ({
  id: raw.id ?? '',
  title: raw.title ?? '',
  mode: raw.mode ?? 'text',
  body: raw.body ?? '',
  groups: Array.isArray(raw.groups) && raw.groups.length > 0
    ? raw.groups
    : [{
      id: raw.id ?? '',
      label: '',
      intro: raw.intro ?? '',
      steps: Array.isArray(raw.steps) && raw.steps.length > 0 ? raw.steps : [],
    }],
});

// ─── SHARED STYLE ────────────────────────────────────────────────

const contentSx = {
  fontFamily: "'Inter', sans-serif",
  fontSize: { xs: '16px', md: '20px' },
  fontWeight: 400,
  color: '#1a1a1a',
  lineHeight: 1.7,
  whiteSpace: 'pre-wrap',
} as const;

// ─── STEP GROUP ──────────────────────────────────────────────────

const StepGroup: React.FC<{
  group: AccordionStepGroup;
  groupIndex: number;
  totalGroups: number;
}> = ({ group, groupIndex, totalGroups }) => {
  const hasSteps = (group.steps ?? []).some((s) => s.text);

  return (
    <Box sx={{ ...(groupIndex < totalGroups - 1 ? { mb: '32px' } : {}) }}>

      {/* Optional bold label e.g. CAUTION */}
      {group.label && (
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: { xs: '16px', md: '20px' },
            fontWeight: 700,
            color: '#1a1a1a',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            mb: '8px',
          }}
        >
          {group.label}
        </Typography>
      )}

      {/* Intro paragraph */}
      {group.intro && (
        <Typography sx={{ ...contentSx, mb: hasSteps ? '20px' : 0 }}>
          {group.intro}
        </Typography>
      )}

      {/* Steps */}
      {hasSteps && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {(group.steps ?? []).filter((s) => s.text).map((step, i) => (
            <Box
              key={step.id}
              sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 3 }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: { xs: '16px', md: '20px' },
                    fontWeight: 700,
                    color: '#1a1a1a',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    mb: '6px',
                  }}
                >
                  STEP {i + 1}
                </Typography>
                <Typography sx={contentSx}>
                  {step.text}
                </Typography>
              </Box>

              {(step.iconUrl || step.iconFilename) && (
                <Box
                  component="img"
                  src={resolveUrl(step.iconUrl || step.iconFilename)}
                  alt={`Step ${i + 1} icon`}
                  sx={{ width: 52, height: 52, objectFit: 'contain', flexShrink: 0, alignSelf: 'center' }}
                />
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

// ─── SINGLE ROW ──────────────────────────────────────────────────

const AccordionRow: React.FC<{ item: AccordionItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);

  return (
    <Box>
      <Box
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pt: '20px',
          pb: '12px',
          cursor: 'pointer',
          userSelect: 'none',
          borderBottom: '1px solid #1a1a1a',
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: { xs: '16px', md: '20px' },
            fontWeight: 700,
            color: '#1a1a1a',
            letterSpacing: '0.01em',
          }}
        >
          {item.title || 'Untitled'}
        </Typography>
        <Box sx={{ flexShrink: 0, ml: 2 }}>
          {open ? (
            <ExpandLess sx={{ fontSize: 20, color: '#1a1a1a' }} />
          ) : (
            <ExpandMore sx={{ fontSize: 20, color: '#1a1a1a' }} />
          )}
        </Box>
      </Box>

      <Collapse in={open} unmountOnExit>
        {item.mode === 'steps' ? (
          <Box sx={{ pt: '20px', pb: '24px' }}>
            {(item.groups ?? []).map((group, gi) => (
              <StepGroup
                key={group.id}
                group={group}
                groupIndex={gi}
                totalGroups={(item.groups ?? []).length}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ pt: '20px', pb: '24px' }}>
            <Typography sx={contentSx}>
              {item.body}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

// ─── MAIN RENDERER ───────────────────────────────────────────────

const AccordionBlockRenderer: React.FC<AccordionBlockRendererProps> = ({ config }) => {
  const items: AccordionItem[] = Array.isArray(config.accordion_items)
    ? config.accordion_items
      .map(migrateItem)
      .filter((i) => i.title || i.body || (i.groups?.length > 0))
    : [];

  if (items.length === 0) return null;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '70%',
        mx: 'auto',

      }}
    >
      {items.map((item) => (
        <AccordionRow key={item.id} item={item} />
      ))}
    </Box>
  );
};

export default AccordionBlockRenderer;